import { AppRoutes, CardType, Languages, ProgramAnalyticsAggregateEnum, WalletAppleLayout, WalletPlatform, WalletSideVisible } from "../../enums";
import { Program } from "../../services/api";

export const APP_FEATURE_KEY = "app";

export interface AnalyticsFilters {
  aggregateType?: ProgramAnalyticsAggregateEnum;
  campaignId?: string;
  from?: string;
  to?: string;
}

/** A field the page error banner links to: the link focuses the element with this id. */
export interface PageErrorField {
  id: string;
  label: string;
}

/** The critical banner at the top of the page (title, the reason, links to invalid fields). Texts or translation keys. */
export interface PageError {
  title: string;
  message?: string;
  fields?: PageErrorField[];
}

/** What Home shows from the rewards and engagements, kept across visits so Home can paint before they reload. */
export interface HomeSummary {
  activeRewards: number;
  earnFlowActive: boolean;
  creditRate: number | null;
}

export interface AppState {
  language: Languages;
  uploadImageLoading: boolean;
  dirtyForm: boolean;
  accessToken: string;
  userId: string;
  iframeHost: string;
  iframeShop: string;
  iframeIdToken: string;
  iframeProgramId: string;
  iframeProgramName: string;
  iframeIntegrationName: Program.IntegrationEnum;
  analyticsFilters: AnalyticsFilters;
  sideNav: {
    open: boolean;
    active: boolean;
    mobile: boolean;
    hasLogOut: boolean;
    collapsed: boolean;
  };
  pages: {
    [AppRoutes.design]: {
      walletPlatform: WalletPlatform;
      walletSideVisible: WalletSideVisible;
      walletAppleLayout: WalletAppleLayout;
    };
    [AppRoutes.locationBased]: {
      walletPlatform: WalletPlatform;
    };
    [AppRoutes.scheduled]: {
      walletPlatform: WalletPlatform;
    };
    [AppRoutes.coupons]: {
      type: CardType;
      walletPlatform: WalletPlatform;
    };
    [AppRoutes.anonymous]: {
      walletPlatform: WalletPlatform;
    };
  };
  homeCompletedSteps: Record<string, boolean>;
  /** Completed setup guides the merchant closed ("online", "physical"): they stay hidden. */
  homeDismissedGuides: Record<string, boolean>;
  /** Highest threshold each Home notice ("usage", "cappedAmount") was dismissed at: it only comes back past it. */
  homeDismissedNotices: Record<string, number>;
  /** Shopify's last answer about the app's extensions in use: Home paints with it while it asks again. */
  homeExtensionHandles: string[] | null;
  homeSummary: HomeSummary | null;
  /** The install the ticks, dismissed guides and last answers above belong to; a new install starts them over. */
  homeInstallKey: string | null;
  serverDown: boolean;
  lastRoute: string | null;
  /** The program exists but its onboarding configuration was not finished (resume on next load). */
  onboardingPending: boolean;
  /** A failed operation or submit, shown in the page's critical banner until dismissed or the page changes. */
  pageError: PageError | null;
  /** Title for the program's formError in that banner, by what failed (a save, a delete); null for anything else. */
  formErrorTitle: string | null;
}

export const initialState: AppState = {
  language: Languages.en,
  uploadImageLoading: false,
  dirtyForm: false,
  accessToken: null,
  userId: null,
  iframeHost: null,
  iframeShop: null,
  iframeIdToken: null,
  iframeProgramId: null,
  iframeProgramName: null,
  iframeIntegrationName: null,
  analyticsFilters: {
    aggregateType: ProgramAnalyticsAggregateEnum.PASSES_INSTALLED,
    campaignId: null,
    from: null,
    to: null,
  },
  sideNav: {
    open: false,
    active: true,
    mobile: false,
    hasLogOut: true,
    collapsed: false,
  },
  pages: {
    [AppRoutes.design]: {
      walletPlatform: WalletPlatform.Apple,
      walletSideVisible: WalletSideVisible.Front,
      walletAppleLayout: WalletAppleLayout.Poster,
    },
    [AppRoutes.locationBased]: {
      walletPlatform: WalletPlatform.Apple,
    },
    [AppRoutes.scheduled]: {
      walletPlatform: WalletPlatform.Apple,
    },
    [AppRoutes.coupons]: {
      type: CardType.notification,
      walletPlatform: WalletPlatform.Apple,
    },
    [AppRoutes.anonymous]: {
      walletPlatform: WalletPlatform.Apple,
    },
  },
  homeCompletedSteps: {},
  homeDismissedGuides: {},
  homeDismissedNotices: {},
  homeExtensionHandles: null,
  homeSummary: null,
  homeInstallKey: null,
  serverDown: false,
  lastRoute: null,
  onboardingPending: false,
  pageError: null,
  formErrorTitle: null,
};

export const persistedKeys: (keyof AppState)[] = [
  "language",
  "accessToken",
  "userId",
  "analyticsFilters",
  "iframeHost",
  "iframeShop",
  "iframeProgramId",
  "iframeIntegrationName",
  "sideNav",
  "homeCompletedSteps",
  "homeDismissedGuides",
  "homeDismissedNotices",
  "homeExtensionHandles",
  "homeSummary",
  "homeInstallKey",
  "lastRoute",
  "onboardingPending",
];
